import defaultBranchIcon from "assets/images/default-branch-icon.png"
import { resolveOrderDeliveryDate } from "utils/orderDeliveryDisplay"
import { resolveSystemOrderStatusValue } from "utils/orderStatusUtils"

const parseFilterDateTimeBound = (value, { endOfRange = false } = {}) => {
  if (!value || typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  const withTime = trimmed.replace(" ", "T")
  const parsed = new Date(withTime)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  if (!/\d{2}:\d{2}/.test(trimmed) && endOfRange) {
    parsed.setHours(23, 59, 59, 999)
  } else if (!/\d{2}:\d{2}/.test(trimmed)) {
    parsed.setHours(0, 0, 0, 0)
  }
  return parsed.getTime()
}

/** Calendar filter range against estimated (scheduled) delivery — not actual deliveredAt. */
export const orderMatchesEstimatedDeliveryDateRange = (
  order,
  estimatedDeliveryDateRange = {}
) => {
  const { min, max } = estimatedDeliveryDateRange || {}
  if (!min || !max) {
    return true
  }
  const minTs = parseFilterDateTimeBound(min)
  const maxTs = parseFilterDateTimeBound(max, { endOfRange: true })
  if (minTs == null || maxTs == null) {
    return true
  }
  const deliveryDate = resolveOrderDeliveryDate(order)
  if (!deliveryDate) {
    return false
  }
  const deliveryTs = new Date(deliveryDate).getTime()
  if (Number.isNaN(deliveryTs)) {
    return false
  }
  return deliveryTs >= minTs && deliveryTs <= maxTs
}

export const DEFAULT_BRANCH_ICON_URL = defaultBranchIcon

export const getOrderDeliveryCoords = order => {
  const lat = order?.recipientInformation?.address?.lat
  const lng = order?.recipientInformation?.address?.lng
  if (lat == null || lng == null || lat === "" || lng === "") return null
  const parsedLat = Number(lat)
  const parsedLng = Number(lng)
  if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) return null
  return { lat: parsedLat, lng: parsedLng }
}

export const getBranchCoords = branch => {
  if (branch?.latitude != null && branch?.longitude != null) {
    const lat = Number(branch.latitude)
    const lng = Number(branch.longitude)
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng }
    }
  }
  const coords = branch?.location?.coordinates
  if (Array.isArray(coords) && coords.length >= 2) {
    const lng = Number(coords[0])
    const lat = Number(coords[1])
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng }
    }
  }
  return null
}

const encodeSvgDataUrl = svg =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`

export const createShopMarkerIcon = () =>
  encodeSvgDataUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="#556ee6" stroke="#fff" stroke-width="1.5"/>
      <path fill="#fff" d="M7 9V7.5C7 6.67 7.67 6 8.5 6h7c.83 0 1.5.67 1.5 1.5V9h1.5c.55 0 1 .45 1 1v1c0 .55-.45 1-1 1h-.18l-.82 7.38c-.08.72-.7 1.27-1.43 1.27H8.43c-.73 0-1.35-.55-1.43-1.27L6.18 12H6c-.55 0-1-.45-1-1v-1c0-.55.45-1 1-1H7zm1.5-.5V9h7V8.5h-7zM8.5 12l.75 6.75h5.5L15.5 12h-7z"/>
    </svg>`
  )

const ORDER_MARKER_COLORS = {
  unassigned: "#f46a6a",
  assigned: "#f1b44c",
  in_progress: "#50a5f1",
  delivered: "#34c38f",
  default: "#74788d",
}

export const getOrderMarkerColor = order => {
  const deliveryStatus = order?.deliveryStatus
  if (deliveryStatus && ORDER_MARKER_COLORS[deliveryStatus]) {
    return ORDER_MARKER_COLORS[deliveryStatus]
  }
  if (order?.deliveryPartnerId || order?.deliveryPartnerName) {
    return ORDER_MARKER_COLORS.assigned
  }
  return ORDER_MARKER_COLORS.unassigned
}

export const createOrderMarkerIcon = color =>
  encodeSvgDataUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M16 0C7.16 0 0 7.16 0 16c0 11.5 16 26 16 26s16-14.5 16-26C32 7.16 24.84 0 16 0z"/>
      <circle cx="16" cy="16" r="7" fill="#fff"/>
    </svg>`
  )

export const buildBranchMarkerIcon = (imageUrl, googleMaps) => {
  if (imageUrl && googleMaps?.Size && googleMaps?.Point) {
    return {
      url: imageUrl,
      scaledSize: new googleMaps.Size(44, 44),
      anchor: new googleMaps.Point(22, 44),
    }
  }
  if (googleMaps?.Size && googleMaps?.Point) {
    return {
      url: DEFAULT_BRANCH_ICON_URL,
      scaledSize: new googleMaps.Size(44, 44),
      anchor: new googleMaps.Point(22, 44),
    }
  }
  return { url: imageUrl || DEFAULT_BRANCH_ICON_URL }
}

export const buildOrderMarkerIcon = (order, googleMaps) => {
  const color = getOrderMarkerColor(order)
  const url = createOrderMarkerIcon(color)
  if (googleMaps?.Size && googleMaps?.Point) {
    return {
      url,
      scaledSize: new googleMaps.Size(32, 42),
      anchor: new googleMaps.Point(16, 42),
    }
  }
  return { url }
}

export const formatOrderAddress = (order, language = "en") => {
  const address = order?.recipientInformation?.address
  if (!address) return ""
  const parts = [
    address.address1 || address.street,
    address.district,
    language === "sa" ? address.cityAr || address.city : address.city,
  ].filter(Boolean)
  return parts.join(", ")
}

const getOrderUser = order => {
  if (!order) return null
  if (order.userId && typeof order.userId === "object") return order.userId
  if (order.userData && typeof order.userData === "object") return order.userData
  if (Array.isArray(order.userData) && order.userData[0]) return order.userData[0]
  return null
}

export const getOrderCustomerName = (order, language = "en") => {
  if (!order) return ""
  if (order.name) return order.name
  const user = getOrderUser(order)
  if (user) {
    const fullName =
      user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim()
    if (fullName) return fullName
  }
  return order.recipientInformation?.recipientName || ""
}

export const getOrderPhone = order => {
  if (!order) return ""
  if (order.phone) return order.phone
  const user = getOrderUser(order)
  if (user) {
    return (
      user.phone ||
      `${user.phoneCode || ""}${user.phoneNumber || ""}`.trim()
    )
  }
  return order.recipientInformation?.recipientPhone || ""
}

/** Only use branch image on map when it is a real uploaded photo, not SVG/placeholder icons */
export const hasCustomBranchImage = imageUrl => {
  if (!imageUrl || !String(imageUrl).trim()) return false
  const url = String(imageUrl).trim().toLowerCase()
  if (url.startsWith("data:")) return false
  return /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url)
}

export const formatMapOrderLabel = orderNo => {
  if (!orderNo) return ""
  const value = String(orderNo).trim()
  if (value.length <= 12) return value
  return value.slice(-10)
}

/** Spread overlapping order pins in a ring so branch markers stay visible. */
export const spreadOrderMarkerPositions = (
  orderMarkers = [],
  branchMarkers = [],
  radiusDeg = 0.00014
) => {
  if (!orderMarkers.length) return []

  const roundCoord = value => Number(Number(value).toFixed(4))
  const branchKeys = new Set(
    branchMarkers.map(
      branch => `${roundCoord(branch.position.lat)},${roundCoord(branch.position.lng)}`
    )
  )

  const groups = new Map()
  orderMarkers.forEach(marker => {
    const baseKey = `${roundCoord(marker.position.lat)},${roundCoord(marker.position.lng)}`
    const key = branchKeys.has(baseKey) ? `branch-${baseKey}` : baseKey
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(marker)
  })

  const spread = []
  groups.forEach(group => {
    if (group.length === 1) {
      spread.push({ ...group[0], displayPosition: group[0].position })
      return
    }

    const center = group[0].position
    const spreadRadius = radiusDeg * Math.min(Math.max(group.length, 3), 10)

    group.forEach((marker, index) => {
      const angle = (2 * Math.PI * index) / group.length - Math.PI / 2
      spread.push({
        ...marker,
        displayPosition: {
          lat: center.lat + spreadRadius * Math.sin(angle),
          lng: center.lng + spreadRadius * Math.cos(angle),
        },
      })
    })
  })

  return spread
}

export const getOrderBranchName = (order, language = "en") => {
  if (!order) return ""
  const branchRef = order.branchId
  const branchFromRef =
    branchRef && typeof branchRef === "object"
      ? language === "sa"
        ? branchRef.nameAr || branchRef.name
        : branchRef.name || branchRef.nameAr
      : ""

  if (language === "sa") {
    return (
      order.branchNameAr ||
      branchFromRef ||
      order.branchName ||
      ""
    )
  }
  return order.branchName || branchFromRef || order.branchNameAr || ""
}

/** Orders shown on map — must match last applied map filters (Search). */
export const filterOrdersForAppliedMapFilters = (
  orders = [],
  appliedMapFilters = {}
) => {
  const {
    branchId = [],
    riderId = "",
    status = 0,
    assignmentStatus = "",
    estimatedDeliveryDateRange = {},
  } = appliedMapFilters

  let list = (orders || []).filter(order => order?.orderType === "delivery")

  if (branchId?.length) {
    const branchIds = new Set(branchId.map(item => String(item.value)))
    list = list.filter(order => {
      const branchRef = order.branchId
      const id = typeof branchRef === "object" ? branchRef?._id : branchRef
      return id && branchIds.has(String(id))
    })
  }

  if (riderId) {
    list = list.filter(order => {
      const partner = order.deliveryPartnerId
      const id = typeof partner === "object" ? partner?._id : partner
      return id && String(id) === String(riderId)
    })
  }

  if (status != null && Number(status) !== 0) {
    list = list.filter(
      order => resolveSystemOrderStatusValue(order) === Number(status)
    )
  }

  if (assignmentStatus === "assigned") {
    list = list.filter(
      order =>
        order.deliveryPartnerId ||
        order.deliveryPartnerName ||
        (order.deliveryStatus && order.deliveryStatus !== "unassigned")
    )
  } else if (assignmentStatus === "unassigned") {
    list = list.filter(
      order =>
        !order.deliveryPartnerId &&
        !order.deliveryPartnerName &&
        (!order.deliveryStatus || order.deliveryStatus === "unassigned")
    )
  }

  if (
    estimatedDeliveryDateRange?.min &&
    estimatedDeliveryDateRange?.max
  ) {
    list = list.filter(order =>
      orderMatchesEstimatedDeliveryDateRange(order, estimatedDeliveryDateRange)
    )
  }

  return list
}
